import React from "react";
import styled, { css } from "styled-components/macro";
import {
  CircleFill,
  PencilSquare,
  ExclamationTriangle,
  Clock,
  Check2Circle,
  InfoCircle,
  ChevronRight,
  Plus,
  Trash,
  ChevronLeft,
  Star,
  StarFill,
  ArrowRightShort,
  ThreeDots,
  Eye
} from "@styled-icons/bootstrap";
import {
  MixinFloatElement,
  MixinFont,
  MixinPlace,
  ILabelProps,
  IPlaceProps,
} from "./mixins";

interface ITitle {
  m?: string;
  fontSize?: string;
}

export const Title = styled.div<ITitle>`
  font-size: ${(props) => props.fontSize || "24px"};
  font-weight: 700;
  color: ${(props) => props.theme.colors.text};
  margin: ${(props) => props.m || "0"};
`;

export const SplitLine = styled.div`
  position: relative;
  width: 100%;
  text-align: center;
  color: ${(props) => props.theme.colors.textMuted};
  & > hr {
    ${MixinFloatElement};
    width: 100%;
    color: ${(props) => props.theme.colors.border};
  }
  & > label {
    position: relative;
    padding: 0 16px;
    background-color: ${(props) => props.theme.colors.bgBody};
  }
`;

export const Label = styled.label<ILabelProps & IPlaceProps>`
  padding: 0;
  ${MixinFont};
  ${MixinPlace};
`;

export const ErrorMessage = styled(Label) <{ error: boolean }>`
  color: transparent;
  ${(props) =>
    props.error &&
    css`
      color: red;
    `};
  font-weight: 400;
  font-size: 0.8em;
`;
interface IButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  readonly main?: boolean;
  readonly normal?: boolean;
  readonly outline?: boolean;
  readonly danger?: boolean;
  readonly outlineSpecial?: boolean;
}

interface IStyledButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  readonly outline?: boolean
  readonly height?: number
  readonly width?: number
  readonly p?: string
}

export const StyledButton = styled.button<IStyledButtonProps>`
  ${MixinFont};
  cursor: pointer;
  background: ${(props) => props.outline ? 'transparent' : props.theme.colors.primary};
  height: ${(props) => props.height ? `${props.height}px` : ''};
  width: ${(props) => props.width ? `${props.width}px` : ''};
  padding: ${(props) => props.p ? props.p : props.theme.sizes.input.padding};
  color: ${(props) => !props.outline ? 'white' : props.theme.colors.primary};
  border: ${(props) => `1px solid ${props.outline ?  props.theme.colors.primary : 'transparent'}`};
  border-radius: 6px;
  font-size: 20px;
  transition: .3s;
  font-weight: 600;
  &:hover {
    background: ${(props) => props.outline ? '#FFF5EF' : '#D7520A'};
  }
  & a {
    color: ${(props) => !props.outline ? 'white' : props.theme.colors.primary};
  }
  &[disabled] {
    opacity: 60%;
  }
`

export const Button = styled.button<IButtonProps & ILabelProps & IPlaceProps>`
  ${MixinFont};
  ${MixinPlace};
  cursor: pointer;
  padding: ${(props) => props.theme.sizes.input.padding};
  ${(props) =>
    props.main &&
    css`
      color: ${(props) => props.theme.colors.place};
      background-color: ${(props) => props.theme.colors.primary};
      border: 1px solid ${(props) => props.theme.colors.primary};
      &:hover {
        background-color: #cb5210;
      }
    `};
  ${(props) =>
    props.normal &&
    css`
      color: ${(props) => props.theme.colors.thirdly};
      background-color: ${(props) => props.theme.colors.secondary};
      border: 1px solid ${(props) => props.theme.colors.secondary};
      &:hover {
        background-color: #f0ddb9;
      }
    `};
  ${(props) =>
    props.outline &&
    css`
      color: ${(props) => props.theme.colors.thirdly};
      background-color: transparent;
      border: 1px solid ${(props) => props.theme.colors.border};
    `};
  ${(props) =>
    props.outlineSpecial &&
    css`
      color: ${(props) => props.theme.colors.primary};
      background-color: transparent;
      border: 1px solid ${(props) => props.theme.colors.primary};
    `};
  ${(props) =>
    props.danger &&
    css`
      color: ${(props) => props.theme.colors.place};
      background-color: #eb5757;
      border: 1px solid #eb5757;
    `};
  &:active:not(:disabled) {
    color: red;
  }
  &:hover:disabled {
    cursor: wait;
  }
  &:hover {
    color: ${(props) => props.theme.colors.text};
  }
  &:disabled {
    opacity: 0.7;
  }
  align-self: flex-end;
`;

export const IcoEdit = styled(PencilSquare) <{ color?: string }>`
  cursor: pointer;
  color: ${(props) => props.color || props.theme.colors.text};
`;

export const IcoEye = styled(Eye) <{ color?: string }>`
  cursor: pointer;
  color: ${(props) => props.color || props.theme.colors.text};
`;

export const TrashIcon = styled(Trash) <{ color?: string }>`
  cursor: pointer;
  color: ${(props) => props.color || props.theme.colors.text};
`;

export const Circle = styled(CircleFill)`
  color: ${(props) => props.theme.colors.text};
  margin: 8px 4px 0 0;
`;

export const IcoInfo = styled(InfoCircle) <{ color?: string }>`
  cursor: pointer;
  color: ${(props) => props.color || props.theme.colors.text};
`;

export const IcoPlus = styled(Plus)`
  color: ${(props) => props.theme.colors.thirdly};
`;

// For Host verify
export const IcoWarning = styled(ExclamationTriangle)`
  color: ${(props) => props.theme.colors.place};
  padding: 0 4px 2px 0;
`;

export const IcoClock = styled(Clock)`
  padding: 0 4px 2px 0;
`;

export const IcoDone = styled(Check2Circle)`
  padding: 0 4px 2px 0;
`;

export const IcoStar = styled(Star)`
  padding: 0 4px 2px 0;
`;

export const IcoStarFill = styled(StarFill)`
  padding: 0 4px 2px 0;
  color: #fde16d;
`;

export const IcoArrowRight = styled(ChevronRight) <{ color?: string }>`
  color: ${(props) => props.color || props.theme.colors.place};
`;
export const IcoArrowLeft = styled(ChevronLeft) <{ color?: string }>`
  color: ${(props) => props.color || props.theme.colors.place};
`;

export const TextBanner = styled.h1`
  color: ${(props) => props.theme.colors.place};
  font-size: ${(props) => props.theme.font.sizeBanner};
  font-weight: ${(props) => props.theme.font.weightBold};
  line-height: 110%;
  margin-top: 15vh;
  font-size: 42px;
  width: 90%;
  @media ${(props) => props.theme.media.phone} {
    font-size: 32px;
    margin-top: 2vh;
  }
`;

export const TitleH1 = styled.h1<{
  m?: string;
  maxWidth?: string;
  positionText?: "start" | "center" | "end";
  display?: string;
  alignItems?: string;
}>`
  font-size: ${(props) => props.theme.font.sizeTitle};
  width: 100%;
  font-weight: ${(props) => props.theme.font.weightBold};
  align-items: ${(props) => props.alignItems};
  display: ${(props) => props.display};
  color: ${(props) => props.theme.colors.text};
  margin: ${(props) => props.m || "0"};
  text-align: ${(props) => props.positionText || "start"};
  max-width: ${(props) => props.maxWidth || "initial"};
`;

export const TitleH2 = styled.h2<{ m?: string }>`
  color: ${(props) => props.theme.colors.text};
  font-size: ${(props) => props.theme.font.sizeSubTitle};
  font-weight: ${(props) => props.theme.font.weightMedium};
  margin: ${(props) => props.m || "0"};
`;

export const TextLink = styled.p`
  color: ${(props) => props.theme.colors.thirdly};
  font-weight: 600;
  cursor: pointer;
`;

export const TextError = styled.p`
  font-size: 18px;
  line-height: 133%;
  color: red;
  word-break: break-all;
  white-space: pre-line;
  @media ${(props) => props.theme.media.phone} {
    font-size: 14px;
  }
`;

export const TitleH3 = styled.h3`
  color: ${(props) => props.theme.colors.text};
  font-size: ${(props) => props.theme.font.size};
  font-weight: ${(props) => props.theme.font.weightMedium};
  margin-bottom: 4px;
`;

export const TitleH4 = styled.h4<{ m?: string }>`
  color: ${(props) => props.theme.colors.text};
  font-size: ${(props) => props.theme.font.size};
  font-weight: ${(props) => props.theme.font.weightNormal};
  margin: ${(props) => props.m || "0"};
`;

export const TitleH18 = styled.h4<{ m?: string }>`
  color: ${(props) => props.theme.colors.text};
  font-size: 18px;
  font-weight: ${(props) => props.theme.font.weightMedium};
  margin: ${(props) => props.m || "0"};
`;

export const TitleH14 = styled.h4<{ m?: string }>`
  color: ${(props) => props.theme.colors.thirdly};
  font-size: 14px;
  font-weight: ${(props) => props.theme.font.weightMedium};
  margin: ${(props) => props.m || "0"};
`;

export const Text18Size = styled.p`
  font-size: 18px;
  line-height: 133%;
  color: ${(props) => props.theme.colors.text};
`;

export const TextMessage = styled.p`
  font-size: 18px;
  line-height: 133%;
  word-break: break-all;
  white-space: normal;
  color: ${(props) => props.theme.colors.text};
  @media ${(props) => props.theme.media.phone} {
    font-size: 14px;
  }
`;


export const TextPlain = styled.p`
  font-size: ${(props) => props.theme.font.size};
  word-break: break-all;
  white-space: normal;
  margin: 0;
  color: ${(props) => props.theme.colors.text};
  @media ${(props) => props.theme.media.phone} {
    font-size: 14px;
  }
`;

export const VerticalLine = styled.div`
  width: 1px;
  height: 100%;
  background: ${(props) => props.theme.colors.border};
  margin: 0 24px;

  @media ${(props) => props.theme.media.phone} {
    margin: 0 8px;
  }
`;

export const IcoShortArrowRight = styled(ArrowRightShort) <{ left?: boolean }>`
  color: ${(props) => props.theme.colors.place};
  ${(props) =>
    props.left &&
    css`
      transform: rotateY(180deg);
    `}
`;

export const IcoThreeDots = styled(ThreeDots) <{ color?: string }>`
  color: ${(props) => (props.color ? props.color : props.theme.colors.place)};
`;
